import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TablePagination,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import * as XLSX from 'xlsx';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  CloudUpload as UploadIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { 
  getAllowedLabTypes, 
  getLabTypeName, 
  getInitialLabTypeId,
  getFirstLabTypeId, 
  shouldDisableDropdown,
  LAB_TYPES 
} from "../../../../utils/labTypeFilter";

const AbreviationMaster = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // Form state
  const [formData, setFormData] = useState({
    abreviationid: null,
    abreviation: '',
    labtypeid: '',
    disciplineid: '',
    groupid: '',
    type: '',
    isactive: 1
  });

  // Dropdown states
  const [disciplines, setDisciplines] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Filter state
  const [filterLabType, setFilterLabType] = useState('');
  const [filterDiscipline, setFilterDiscipline] = useState('');
  const [filterDisciplines, setFilterDisciplines] = useState([]);
  const [filterGroup, setFilterGroup] = useState('');
  const [filterGroups, setFilterGroups] = useState([]);
  const [searchText, setSearchText] = useState('');

  // Pagination state - server-side
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Table state
  const [abreviationData, setAbreviationData] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Lab Type options
  const labTypes = getAllowedLabTypes();

  // Get labTypeId from localStorage and set in form data
  useEffect(() => {
    const initialLabTypeId = getInitialLabTypeId();
    const firstLabTypeId = getFirstLabTypeId();
    
    if (initialLabTypeId) {
      setFormData(prev => ({
        ...prev,
        labtypeid: initialLabTypeId
      }));
    }
    
    if (firstLabTypeId) {
      setFilterLabType(firstLabTypeId);
    }
  }, []);

  // Fetch disciplines when lab type changes
  useEffect(() => {
    if (formData.labtypeid) {
      fetchDisciplines(formData.labtypeid);
      setFormData(prev => ({ ...prev, disciplineid: '', groupid: '' }));
      setGroups([]);
    }
  }, [formData.labtypeid]);

  // Fetch groups when discipline changes
  useEffect(() => {
    if (formData.labtypeid && formData.disciplineid) {
      fetchGroups(formData.labtypeid, formData.disciplineid);
      setFormData(prev => ({ ...prev, groupid: '' }));
    }
  }, [formData.labtypeid, formData.disciplineid]);

  // Fetch equipment list when page, filters, or search changes
  useEffect(() => {
    fetchAbreviationList();
  }, [page, rowsPerPage, filterLabType, filterDiscipline, filterGroup, searchText]);

  // Handle filter disciplines when filter lab type changes
  useEffect(() => {
    if (filterLabType) {
      fetchFilterDisciplines(filterLabType);
      setFilterDiscipline('');
      setFilterGroup('');
      setPage(0); // Reset to first page when filter changes
    } else {
      setFilterDisciplines([]);
      setFilterDiscipline('');
      setFilterGroups([]);
      setFilterGroup('');
    }
  }, [filterLabType]);

  // Fetch groups for filter dropdown when discipline changes (only for Testing and Calibration)
  useEffect(() => {
    if (filterDiscipline && (filterLabType === 1 || filterLabType === 2)) {
      fetchFilterGroups(filterLabType, filterDiscipline);
    } else {
      setFilterGroups([]);
      setFilterGroup('');
    }
  }, [filterDiscipline, filterLabType]);

  // Reset page when discipline filter changes
  useEffect(() => {
    if (filterDiscipline) {
      setPage(0);
    }
  }, [filterDiscipline]);

  // Debounced search
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (searchText !== undefined) {
        setPage(0); // Reset to first page when searching
        // fetchEquipmentList will be triggered by the page dependency
      }
    }, 500);

    return () => clearTimeout(delayedSearch);
  }, [searchText]);

  // Fetch equipment list with pagination and filtering
  const fetchAbreviationList = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...(filterLabType && { labTypeId: filterLabType }),
        ...(filterDiscipline && { disciplineId: filterDiscipline }),
        ...(filterGroup !== '' && filterGroup != null && { groupId: filterGroup }),
        ...(searchText && { search: searchText.trim() })
      };
      console.log('Fetching abreviations with params:', params);
      console.log('filterGroup value:', filterGroup, 'type:', typeof filterGroup);
      const response = await axios.get(`${API_BASE_URL}/admin-pt-master/abreviations`, { params });
      if (response.data.status === 'success') {
        setAbreviationData(response.data.data || []);
        setTotalCount(response.data.totalCount || 0);
        setError('');
      } else {
        setError('Failed to fetch abreviation list');
        setAbreviationData([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('Error fetching abreviation:', err);
      setError(err.response?.data?.message || 'Error fetching abreviation list');
      setAbreviationData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleResetFilter = () => {
    setFilterDiscipline('');
    setFilterGroup('');
    setSearchText('');
    setPage(0);
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);

      const params = {
        page: page + 1,
        limit: 1000000,
        ...(filterLabType && { labTypeId: filterLabType }),
        ...(filterDiscipline && { disciplineId: filterDiscipline }),
        ...(filterGroup !== '' && filterGroup != null && { groupId: filterGroup }),
        ...(searchText && { search: searchText.trim() })
      };

      const response = await axios.get(
        `${API_BASE_URL}/admin-pt-master/abreviations`,
        { params }
      );

      if (response.data.status !== "success") {
        setError("Failed to export data");
        return;
      }

      const allData = response.data.data || [];

      const exportData = allData.map((row, index) => {
        const labType = labTypes.find(lab => String(lab.id) === String(row.labtypeid));
        return {
          "S.No": index + 1,
          "CAB Type": labType?.name || "N/A",
          "Abreviation": row.Abreviation || "N/A",
          "Abreviation Type": row.Type || "N/A",
          "Discipline": row.DisciplineName || "N/A",
          "Group": row.groupName || "N/A",
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, "abreviationData");

      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[:T]/g, "-");
      XLSX.writeFile(workbook, `Abreviation_Master_${timestamp}.xlsx`);
    } catch (err) {
      console.error(err);
      setError("Error exporting Excel");
    } finally {
      setLoading(false);
    }
  };

  // Fetch disciplines by lab type for form
  const fetchDisciplines = async (labTypeId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin-master/disciplines/${labTypeId}`);

      if (response.data.status === 'success') {
        setDisciplines(response.data.data || []);
      } else {
        setDisciplines([]);
      }
    } catch (err) {
      console.error('Error fetching disciplines:', err);
      setDisciplines([]);
    }
  };

  // Fetch disciplines for filter dropdown
  const fetchFilterDisciplines = async (labTypeId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin-master/disciplines/${labTypeId}`);

      if (response.data.status === 'success') {
        setFilterDisciplines(response.data.data || []);
      } else {
        setFilterDisciplines([]);
      }
    } catch (err) {
      console.error('Error fetching filter disciplines:', err);
      setFilterDisciplines([]);
    }
  };

  // Fetch groups for filter dropdown
  const fetchFilterGroups = async (labTypeId, disciplineId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin-master/groups`, {
        params: {
          labTypeId,
          disciplineId
        }
      });

      if (response.data.status === 'success') {
        // Transform data: API returns grpId as string, convert to number
        const transformedData = (response.data.data || []).map(item => ({
          ...item,
          grpId: Number(item.grpId),
        }));
        console.log('Fetched filter groups:', transformedData);
        setFilterGroups(transformedData);
      } else {
        setFilterGroups([]);
      }
    } catch (err) {
      console.error('Error fetching filter groups:', err);
      setFilterGroups([]);
    }
  };

  // Fetch groups by discipline
  const fetchGroups = async (labTypeId, disciplineId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin-master/groups`, {
        params: {
          labTypeId,
          disciplineId
        }
      });

      if (response.data.status === 'success') {
        // Transform data: API returns grpId as string, convert to number
        const transformedData = (response.data.data || []).map(item => ({
          ...item,
          grpId: Number(item.grpId),
        }));
        console.log('Fetched groups:', transformedData);
        setGroups(transformedData);
      } else {
        setGroups([]);
      }
    } catch (err) {
      console.error('Error fetching groups:', err);
      setGroups([]);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle form submission (Create/Update)
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.abreviation || !formData.labtypeid || !formData.disciplineid || !formData.type) {
      setError('Abreviation, Lab Type, Discipline, and Type are required');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const payload = {
        abreviation: formData.abreviation,
        labtypeid: Number(formData.labtypeid),
        disciplineid: Number(formData.disciplineid),
        type: formData.type,
        groupid: formData.groupid || null,
        isactive: formData.isactive
      };
      const response = await axios.post(`${API_BASE_URL}/admin-pt-master/abreviations`, payload);
      if (response.data.status === 'success') {
        setSuccessMessage('Abbreviations created successfully!');
        handleReset();
        fetchAbreviationList();
        setIsEditMode(false);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.data.message || 'Failed to save Abreviations');
      }
    } catch (err) {
      console.error('Error saving Abreviations:', err);
      setError(err.response?.data?.message || 'Error saving Abreviations');
    } finally {
      setLoading(false);
    }
  };

  // Handle form reset
  const handleReset = () => {
    const currentLabTypeId = formData.labtypeid;
    
    setFormData({
      abreviationid: null,
      abreviation: '',
      labtypeid: currentLabTypeId,  // Keep the current lab type
      disciplineid: '',
      groupid: '',
      type: '',
      isactive: 1
    });
    setIsEditMode(false);
    
    // Re-fetch disciplines for the current lab type
    if (currentLabTypeId) {
      fetchDisciplines(currentLabTypeId);
    } else {
      setDisciplines([]);
    }
    setGroups([]);
  };

  // Handle delete
  const handleDeleteClick = (eqid) => {
    setDeleteId(eqid);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      const response = await axios.delete(`${API_BASE_URL}/admin-pt-master/abreviations/${deleteId}`);

      if (response.data.status === 'success') {
        setSuccessMessage('Equipment deleted successfully!');
        fetchAbreviationList();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.data.message || 'Failed to delete equipment');
      }
    } catch (err) {
      console.error('Error deleting equipment:', err);
      setError(err.response?.data?.message || 'Error deleting equipment');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setDeleteId(null);
    }
  };

  // Handle table pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          fontWeight: 600,
          color: '#1976d2',
          mb: 4
        }}
      >
        Abbreviation Master
      </Typography>

      {/* Alert Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {/* Form Section */}
      <Card elevation={3} sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            {isEditMode ? 'Edit Abbreviation' : 'Add New Abbreviation'}
          </Typography>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Abreviation */}
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Abbreviation"
                  name="abreviation"
                  value={formData.abreviation}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                  placeholder="e.g., TSWST"
                />
              </Grid>
              {/* Type Dropdown */}
              <Grid item xs={12} md={3}>
                <Select
                  fullWidth
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  displayEmpty
                  required
                  variant="outlined"
                >
                  <MenuItem value="" disabled>
                    <em>--Select Type--</em>
                  </MenuItem>
                  <MenuItem value="Equipment">Equipment</MenuItem>
                  <MenuItem value="Scope">Scope</MenuItem>
                </Select>
              </Grid>

              {/* Lab Type */}
              <Grid item xs={12} md={4}>
                <Select
                  fullWidth
                  name="labtypeid"
                  value={formData.labtypeid}
                  onChange={handleInputChange}
                  displayEmpty
                  required
                  disabled={shouldDisableDropdown(labTypes)}
                  variant="outlined"
                >
                  <MenuItem value="" disabled>
                    <em>--Select Lab Type--</em>
                  </MenuItem>
                  {labTypes.map((lab) => (
                    <MenuItem key={lab.id} value={lab.id}>
                      {lab.name}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              {/* Discipline Name */}
              <Grid item xs={12} md={4}>
                <Select
                  fullWidth
                  name="disciplineid"
                  value={formData.disciplineid}
                  onChange={handleInputChange}
                  displayEmpty
                  required
                  variant="outlined"
                  disabled={!formData.labtypeid}
                >
                  <MenuItem value="" disabled>
                    <em>--Select Discipline--</em>
                  </MenuItem>
                  {disciplines.map((discipline) => (
                    <MenuItem key={discipline.id} value={discipline.id}>
                      {discipline.disciplineName}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              {/* Group */}
              <Grid item xs={12} md={4}>
                <Select
                  fullWidth
                  name="groupid"
                  value={formData.groupid}
                  onChange={handleInputChange}
                  displayEmpty
                  variant="outlined"
                  disabled={!formData.disciplineid}
                >
                  <MenuItem value="">
                    <em>--Select Group--</em>
                  </MenuItem>
                  {groups.map((group) => (
                    <MenuItem key={group.grpId} value={group.grpId}>
                      {group.groupName}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            </Grid>

            {/* Form Buttons */}
            <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                disabled={loading}
                sx={{ backgroundColor: '#1976d2' }}
              >
                {isEditMode ? 'Update' : 'Save'}
              </Button>

              <Button
                variant="outlined"
                onClick={handleReset}
                startIcon={<RefreshIcon />}
                disabled={loading}
              >
                Reset
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>

      {/* Abreviation Details Section */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
          Abbreviation Details
        </Typography>

        {/* Filters */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Lab Type Filter */}
          <Grid item xs={12} md={3}>
            <Select
              fullWidth
              displayEmpty
              disabled={shouldDisableDropdown(labTypes)}
              value={filterLabType}
              onChange={(e) => {
                setFilterLabType(e.target.value);
                setFilterDiscipline('');
                setFilterGroup('');
                setPage(0);
                if (e.target.value) {
                  fetchFilterDisciplines(e.target.value);
                } else {
                  setFilterDisciplines([]);
                  setFilterGroups([]);
                }
              }}
              renderValue={(selected) => {
                if (selected === '') {
                  return <em>--All Lab Types--</em>;
                }
                const labType = labTypes.find(l => l.id === selected);
                return labType?.name;
              }}
            >
              <MenuItem value="">
                <em>--All Lab Types--</em>
              </MenuItem>
              {labTypes.map((lab) => (
                <MenuItem key={lab.id} value={lab.id}>
                  {lab.name}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          {/* Discipline Filter */}
          <Grid item xs={12} md={3}>
            <Select
              fullWidth
              displayEmpty
              value={filterDiscipline}
              onChange={(e) => {
                setFilterDiscipline(e.target.value);
                setFilterGroup('');
                setPage(0);
              }}
              disabled={!filterLabType}
              renderValue={(selected) => {
                if (selected === '') {
                  return <em>--All Disciplines--</em>;
                }
                const discipline = filterDisciplines.find(d => String(d.id) === String(selected));
                return discipline?.disciplineName || 'N/A';
              }}
            >
              <MenuItem value="">
                <em>--All Disciplines--</em>
              </MenuItem>
              {filterDisciplines.map((discipline) => (
                <MenuItem key={discipline.id} value={discipline.id}>
                  {discipline.disciplineName}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          {/* Group Filter - Only for Testing and Calibration */}
          {(filterLabType === 1 || filterLabType === 2) && (
            <Grid item xs={12} md={3}>
              <Select
                fullWidth
                displayEmpty
                value={filterGroup}
                onChange={(e) => {
                  setFilterGroup(e.target.value);
                  setPage(0);
                }}
                disabled={!filterDiscipline}
                renderValue={(selected) => {
                  if (selected === '') {
                    return <em>--All Groups--</em>;
                  }
                  const group = filterGroups.find(g => String(g.grpId) === String(selected));
                  return group?.groupName || 'N/A';
                }}
              >
                <MenuItem value="">
                  <em>--All Groups--</em>
                </MenuItem>
                {filterGroups.map((group) => (
                  <MenuItem key={group.grpId} value={group.grpId}>
                    {group.groupName}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          )}

          {/* Search */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Search abbreviation"
              variant="outlined"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search by abbreviation, discipline..."
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              startIcon={<RefreshIcon />}
              onClick={handleResetFilter}
              sx={{ height: "40px" }}
            >
              Reset Filter
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<DownloadIcon />}
              onClick={handleExportExcel}
              sx={{ height: "40px" }}
            >
              Export Excel
            </Button>
          </Grid>
        </Grid>

        {/* Results Counter */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="textSecondary">
            {loading ? 'Loading...' : `Showing ${abreviationData.length} of ${totalCount} abreviations`}
          </Typography>
        </Box>
        {loading && !abreviationData.length ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableRow>
                    <TableCell><strong>S.no</strong></TableCell>
                    <TableCell><strong>Abbreviation</strong></TableCell>
                    <TableCell><strong>Lab Type</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Discipline</strong></TableCell>
                    <TableCell><strong>Group</strong></TableCell>
                    <TableCell><strong>Action</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {abreviationData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography color="textSecondary">
                          {loading ? 'Loading abbreviation data...' : 'No abbreviation data available'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    abreviationData.map((row, index) => {
                      const labType = labTypes.find(lab => String(lab.id) === String(row.labtypeid));
                      return (
                        <TableRow key={row.id}>
                          <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                          <TableCell>{row.Abreviation || ''}</TableCell>
                          <TableCell>{labType?.name || 'N/A'}</TableCell>
                          <TableCell>{row.Type || ''}</TableCell>
                          <TableCell>{row.DisciplineName || 'N/A'}</TableCell>
                          <TableCell>{row.groupName || 'N/A'}</TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(row.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {/* Pagination */}
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ borderTop: '1px solid #e0e0e0' }}
              showFirstButton
              showLastButton
            />
          </>
        )}
      </Paper>
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this equipment?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={loading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AbreviationMaster;